import * as CL from '@design-system/component-library';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { paths } from '../../../common/constants/pathVariables.js';
import { I18nTextLinkEmbedded, t, transferOfSubToAnotherBillingAccountMsg } from '../../../common/i18n/index.js';
import { BillingAccount } from '../../../common/react-hook-form/fields/BillingAccount.js';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../../common/utils/billingAccountUtils.js';
import { BillingAccountFieldset } from '../../BillingAccount/BillingAccountFieldset.js';
import { DeliveryMethodFieldset } from '../../BillingAccount/DeliveryMethodFieldset.js';
import { CompanySelector } from '../../CompanySelector/CompanySelector.js';
import { getUserAccounts } from '../../Header/dynamic/headerFunctions.js';
import type { BillChannel } from '../../../generated/api/billChannel.js';
import type { BillingAccountSearchResponse } from '../../../generated/api/billingAccountSearchResponse.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { AuthenticatedUser } from '../../../public/site/authState.js';

interface SubscriptionBillingAccountFieldsProps {
  name: string;
  billingAccounts: BillingAccountSearchResponse[];
  authenticatedUser?: AuthenticatedUser;
  showCompanySelector: boolean;
  onCompanyChange: (mdmId: string, companyName: string) => Promise<void>;
  disabled: boolean;
  enableAddNewBa: boolean;
  billChannels: BillChannel[];
  contacts: Contact[];
  isChangeContractOwner: boolean;
  isPurposeOfUseSubscription: boolean;
  originalBillingAccountId?: string;
  onCreateBillingContact: () => void;
  onCreateBillReceiverContact: () => void;
  targetAccountId?: string;
  isDirectoryListingDisabled?: boolean;
  onBillingAccountChange?: (billingAccountId: string) => void;
}

export const SubscriptionBillingAccountFields = ({
  name,
  billingAccounts,
  authenticatedUser,
  showCompanySelector,
  onCompanyChange,
  disabled,
  enableAddNewBa,
  billChannels,
  contacts,
  isChangeContractOwner,
  isPurposeOfUseSubscription,
  originalBillingAccountId,
  onCreateBillingContact,
  onCreateBillReceiverContact,
  targetAccountId,
  isDirectoryListingDisabled,
  onBillingAccountChange,
}: SubscriptionBillingAccountFieldsProps) => {
  const methods = useFormContext();
  const targetBillingAccount = methods.watch(name);
  return (
    <>
      <CL.Grid>
        {showCompanySelector && (
          <CL.GridRow className="of-billingAccount-userinfo-form__row">
            <CL.GridCol colWidth={6}>
              <CompanySelector
                labelText={t.HCIE('Contract party')}
                disabled={disabled}
                userAccounts={getUserAccounts(authenticatedUser)}
                initialMdmId={targetAccountId}
                readOnlyIncludeHeader={true}
                onInputChange={async (_, option) => {
                  if (option.accountMasterId && option.name) {
                    await onCompanyChange(option.accountMasterId, option.name);
                  }
                }}
              />
            </CL.GridCol>
          </CL.GridRow>
        )}
        {isChangeContractOwner && (
          <CL.GridRow className="of-billingAccount-userinfo-form__row">
            <CL.GridCol colWidth={12}>
              <CL.Disclaimer icon={<CL.Icon icon="information" size="s" />} visible>
                <p>
                  {isPurposeOfUseSubscription ? (
                    t.M16R(
                      'You are changing the contracting company. The billing account must also be updated according to the new contracting company.'
                    )
                  ) : (
                    <I18nTextLinkEmbedded
                      translation={t.CPDO(
                        'You are changing the contracting company. The billing account and user must also be updated according to the new contracting company. If you want to move the user to another company, you can do so on the [Users] page.'
                      )}
                      linkTo={paths.COMPANY_INFO_CONTACTS}
                    />
                  )}
                </p>
              </CL.Disclaimer>
            </CL.GridCol>
          </CL.GridRow>
        )}

        <CL.GridRow className="of-billingAccount-userinfo-form__row">
          <CL.GridCol colWidth={6}>
            <BillingAccount
              name={name}
              enableAddNewBa={enableAddNewBa}
              billingAccounts={billingAccounts}
              onChange={onBillingAccountChange}
            />
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>

      {originalBillingAccountId && originalBillingAccountId !== targetBillingAccount && (
        <CL.GridRow className="of-billingAccount-userinfo-form__row">
          <CL.GridCol colWidth={12}>
            <CL.Disclaimer
              className={dsClass.MARGIN_BOTTOM_5}
              title={t.V2E6(transferOfSubToAnotherBillingAccountMsg)}
              icon={<CL.Icon icon="information" size="s" />}
              visible
            >
              <p>
                {t.AIUJ(
                  'Please note that, after the change, invoicing will still be based on two invoices for the next payment. Any supplementary services will be transferred with the main product.'
                )}
              </p>
              {isDirectoryListingDisabled && (
                <>
                  <p className={dsClass.FONT_WEIGHT_BOLD}>{t.W43T('Directory status')}</p>
                  <p>
                    {t.C4FQ(
                      'Directory status of number cannot be changed when the billing account changes. You can change the directory status after the billing account has changed.'
                    )}
                  </p>
                </>
              )}
            </CL.Disclaimer>
          </CL.GridCol>
        </CL.GridRow>
      )}

      {targetBillingAccount === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && billChannels && contacts && (
        <div className={dsClass.PADDING_LEFT_3}>
          <BillingAccountFieldset
            billChannels={billChannels}
            contacts={contacts}
            onCreateBillingContact={onCreateBillingContact}
          />
          <DeliveryMethodFieldset
            billChannels={billChannels}
            contacts={contacts}
            onCreateBillReceiverContact={onCreateBillReceiverContact}
          />
        </div>
      )}
    </>
  );
};
